import { AsyncPipe, DatePipe, DecimalPipe, KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { Observable } from 'rxjs';
import { combineLatestWith, map, take } from 'rxjs/operators';
import { BasketTableColumns, ProductLine, StoreType } from 'src/app/models';
import { ShippingQuery } from 'src/app/services/shipping';
import {
  CenterSectionComponent,
  PaymentOptionsComponent,
  TextButtonComponent,
  TextInputComponent,
  ToggleComponent,
} from 'src/app/shared/components';
import { DisplayCurrencyPipe } from '../../../../shared/pipes/display-currency.pipe';
import { BasketOverviewUtility } from '../basket-overview.utility';
import { BasketOverviewItemComponent } from './overview-item/basket-overview-item.component';

@Component({
  selector: 'app-basket-overview',
  templateUrl: './basket-overview.component.html',
  styleUrls: ['./basket-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TextButtonComponent,
    MatIconModule,
    CenterSectionComponent,
    KeyValuePipe,
    AsyncPipe,
    BasketOverviewItemComponent,
    DecimalPipe,
    DisplayCurrencyPipe,
    ToggleComponent,
    DatePipe,
    ReactiveFormsModule,
    TextInputComponent,
    MatFormFieldModule,
    PaymentOptionsComponent,
  ],
})
export class BasketOverviewComponent extends BasketOverviewUtility implements OnInit {
  private shippingQuery = inject(ShippingQuery);

  allowCustomerPackaging: Observable<boolean>;
  productLines$: Observable<Map<string, ProductLine[]>>;

  columns: BasketTableColumns;
  simpleOverviewItems: boolean;
  buyMoreButton: string;
  customerPackagingLabel: string;

  noteLabel: string;
  noteSubtitle: string;
  notePlaceholder: string;
  eventShop: boolean;

  missingShippingText: string;

  constructor() {
    super();

    this.buyMoreButton = this.content.get('buyMoreButtonText');

    this.columns = {
      name: this.content.get('productNameColumnLabelText'),
      price: this.content.get('itemPriceColumnLabelText'),
      total: this.content.get('productTotalPriceLabelText'),
      quantity: this.content.get('quantity'),
      delete: this.content.get('removeItemColumnLabelText'),
    };
    this.simpleOverviewItems = this.content.get('simpleOverviewItems');

    this.customerPackagingLabel = this.content.get('customerPackagingLabelText') || 'Medbring egen emballage';
    this.missingShippingText = this.content.get('missingShippingText') || 'Vælg hvordan du vil have varerne';

    const basketShipping = this.contentService.get('basketShipping');
    this.noteLabel = basketShipping.get('billingNoteLabelText');
    this.noteSubtitle = basketShipping.get('billingNoteSubtitleText');
    this.notePlaceholder = basketShipping.get('billingNotePlaceholderText');
  }

  override ngOnInit() {
    super.ngOnInit();

    this.eventShop = this.contentService.isEventStore;
    this.allowCustomerPackaging = this.shippingQuery.selectCustomerPackaging$;

    this.productLines$ = this.basketQuery.selectProductLines$.pipe(
      combineLatestWith(this.basketQuery.selectErrorProductIds$),
      map(([productLines, invalidProductIds]) => {
        const productCategoryMap = new Map<string, ProductLine[]>();

        for (const line of productLines) {
          const product = this.basketService.getProduct(line);
          if (!product) {
            continue;
          }

          const category = this.categoryService.getById(product.primaryCategoryId)?.data.name;

          if (!productCategoryMap.has(category)) {
            productCategoryMap.set(category, []);
          }

          const error = invalidProductIds.includes(line.productId);

          const update = productCategoryMap.get(category);
          const entry = { ...line, product, error };
          update.push(entry);
          productCategoryMap.set(category, update);
        }

        return productCategoryMap;
      }),
    );

    this.cdr.detectChanges();
  }

  removeInvalidBasket() {
    const ids = this.basketQuery.getErrorProductIds();
    this.productLines$.pipe(take(1)).subscribe((productLines) => {
      for (const [_, lines] of productLines) {
        for (const line of lines) {
          if (ids.includes(line.productId)) {
            this.basketService.deleteProduct(line.product);
          }
        }
      }
    });
  }
}
